const BASE_IMAGE_URL = process.env.REACT_APP_BASE_IMAGE_URL || "";

/**
 * Some APIs return a stored file as a relative S3 key
 * ("users/1/operating_agreements/foo.pdf") and others as an absolute S3 URL
 * ("https://itin-dev.s3.../users/1/operating_agreements/foo.pdf").
 * The `/download/` endpoint presigns a *key*, so an absolute URL has to be
 * reduced back to its key before it is sent.
 */
export const toStorageKey = (url?: string | null): string => {
  if (!url) return "";
  const value = String(url).trim();
  if (!value || value === "null" || value === "undefined") return "";

  if (BASE_IMAGE_URL && value.startsWith(BASE_IMAGE_URL)) {
    return value.slice(BASE_IMAGE_URL.length);
  }
  if (/^https?:\/\//i.test(value)) {
    try {
      return decodeURIComponent(new URL(value).pathname.replace(/^\//, ""));
    } catch {
      return value;
    }
  }
  return value;
};

/**
 * Encodes a key for use in `?url=`. Path separators are left intact so keys
 * without special characters serialise exactly as they did before, while
 * spaces / parentheses (which otherwise truncate the query value) are escaped.
 */
export const encodeStorageKey = (key: string): string =>
  key.split("/").map(encodeURIComponent).join("/");

/** Absolute URL used to preview a stored file in the browser. */
export const toPreviewUrl = (url?: string | null): string => {
  if (!url) return "";
  const value = String(url).trim();
  if (!value || value === "null" || value === "undefined") return "";
  if (/^(https?:|blob:|data:)/i.test(value)) return value;
  return `${BASE_IMAGE_URL}${value}`;
};

/**
 * Resolves the link to open when viewing an invoice.
 *
 * The three candidate fields are not interchangeable:
 *  - `paid_invoice_pdf` / `payment_url` are absolute Stripe URLs
 *  - `invoice_pdf` is an S3 *key* and needs the bucket prefix
 *
 * A paid invoice should show Stripe's receipt; an unpaid one should show the
 * hosted invoice page so it can actually be paid. The S3 PDF is only a
 * last-resort snapshot, and is frequently null. Returns "" when nothing is
 * available, so callers can tell the user instead of opening a broken tab.
 */
export const getInvoiceViewUrl = (invoice: any): string => {
  if (!invoice) return "";
  const isPaid = invoice.invoice_status === 1;
  const candidates = isPaid
    ? [invoice.paid_invoice_pdf, invoice.payment_url, invoice.invoice_pdf]
    : [invoice.payment_url, invoice.paid_invoice_pdf, invoice.invoice_pdf];

  for (const candidate of candidates) {
    const url = toPreviewUrl(candidate);
    if (url) return url;
  }
  return "";
};
